/**
 * ProtectedRoute Component
 * Handles route protection based on authentication and role-based access
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes';
import { Loader2 } from 'lucide-react';

/**
 * ProtectedRoute - Wraps routes that require authentication
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string[]} props.allowedRoles - Array of roles that can access this route
 * @param {string} props.redirectTo - Custom redirect path for unauthorized access
 * @param {boolean} props.requireAuth - Whether authentication is required (default: true)
 */
export function ProtectedRoute({ 
  children, 
  allowedRoles = [], 
  redirectTo,
  requireAuth = true 
}) {
  const { isAuthenticated, loading, currentUser, checkRouteAccess, getDefaultRoute } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If authentication is required and user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Redirect to login, saving the attempted URL
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // If specific roles are required
  if (allowedRoles.length > 0 && currentUser) {
    const hasAccess = allowedRoles.includes(currentUser.role);
    
    if (!hasAccess) {
      // Redirect to user's default route or custom redirect
      const redirectPath = redirectTo || getDefaultRoute();
      return <Navigate to={redirectPath} replace />;
    }
  }

  // Check route access using permissions system
  if (currentUser && !checkRouteAccess(location.pathname)) {
    const redirectPath = redirectTo || getDefaultRoute();
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

/**
 * AdminRoute - Shorthand for routes only accessible by admins
 */
export function AdminRoute({ children, redirectTo }) {
  return (
    <ProtectedRoute allowedRoles={['ADMIN']} redirectTo={redirectTo}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * UserRoute - Shorthand for routes accessible by regular users
 */
export function UserRoute({ children, redirectTo }) {
  return (
    <ProtectedRoute allowedRoles={['ADMIN', 'USER']} redirectTo={redirectTo}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * ReviewerRoute - Shorthand for routes accessible by reviewers
 */
export function ReviewerRoute({ children, redirectTo }) {
  return (
    <ProtectedRoute allowedRoles={['ADMIN', 'REVIEWER']} redirectTo={redirectTo}>
      {children}
    </ProtectedRoute>
  );
}

/**
 * PublicRoute - For routes that should redirect authenticated users away
 * (e.g., login page should redirect to dashboard if already logged in)
 */
export function PublicRoute({ children }) {
  const { isAuthenticated, loading, getDefaultRoute } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect authenticated users to their default route
  if (isAuthenticated) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  return children;
}

export default ProtectedRoute;
